import React, { useState, useEffect } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { checkZipCode } from '../../../Utils/checkZipCode';
import Row from 'react-bootstrap/Row';
import Buttons from '../../../Components/Buttons/Buttons';
import DashboardHeader from '../../../Components/DashboardHeader/DashboardHeader';
import { countries, allStates } from '../../../Components/CountryStateData/CountryStateData';
import './AccountInformation.css';
import { useApi } from "../../../Services/api";
import CustomSelect from '../../../Components/CustomSelect/CustomSelect';
import PhoneNumber from '../../../Components/PhoneNumber/PhoneNumber';
import { useGlobalState } from '../../../Contexts/GlobalStateContext';
import { useClientUserState } from '../../../Contexts/ClientUserStateContext';
import Popup from '../../../Components/Popup/Popup';

const customStyles = {
  control: (provided) => ({
    ...provided,
    width: '100%',
    height: '50px',
    borderColor: '#ADAFB3',
    minWidth: '120px',
    minHeight: '50px',
  })
};

const AccountInformation = () => {
  const [companyName, setCompanyName] = useState('');
  const [companyAddressLine1, setCompanyAddressLine1] = useState('');
  const [companyAddressLine2, setCompanyAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState(null);
  const [country, setCountry] = useState({ label: 'United States', value: '1' });
  const [zipCode, setZipCode] = useState('');
  const [validated, setValidated] = useState(false);
  const [stateInvalid, setStateInvalid] = useState(false);
  const [zipCodeInvalid, setZipCodeInvalid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [zipCodeError, setZipCodeError] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [companyAddressLine1Error, setCompanyAddressLine1Error] = useState('');
  const [cityError, setCityError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  const [isDisable, setIsDisable] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const api = useApi();
  const filteredStates = allStates.filter((state) => state.country === country.value);

  // Contexts
  const { clientUser } = useClientUserState();  
  const { setErrorMessage, setErrorVisible } = useGlobalState();

  useEffect(() => {
    const getInfo = async () => {
      try {
        const getInfoData = await api.get(`Client/SelfSignUpClientUser`);
        const getInfoItems = getInfoData.data;
        // make phone number without the first number
        const initialValues = {
          companyName: getInfoItems.ClientName,
          companyAddressLine1: getInfoItems.CompanyAddressLine1,
          companyAddressLine2: getInfoItems.CompanyAddressLine2,
          city: getInfoItems.City,
          zipCode: getInfoItems.ZipCode,
          firstName: getInfoItems.FirstName,
          lastName: getInfoItems.LastName,
          email: getInfoItems.Email,
          phoneNumber: "+1"+getInfoItems.PhoneNumber,
          country: countries.find(country => country.value === getInfoItems.CountryId.toString()) || { label: 'United States', value: '1' },
          stateProvince: allStates.find(state => state.value === getInfoItems.StateAndProvince) || null,
        };
        setInitialValues(initialValues);
        setCompanyName(initialValues.companyName);
        setCompanyAddressLine1(initialValues.companyAddressLine1);
        setCompanyAddressLine2(initialValues.companyAddressLine2);
        setCity(initialValues.city);
        // gap only for canada
        if (initialValues.country.value === '38') {
          initialValues.zipCode = initialValues.zipCode.slice(0, 3) + ' ' + initialValues.zipCode.slice(3);
        } else {
          initialValues.zipCode = initialValues.zipCode;
        }
        setZipCode(initialValues.zipCode);
        setFirstName(initialValues.firstName);
        setLastName(initialValues.lastName);
        setEmail(initialValues.email);
        setPhoneNumber(initialValues.phoneNumber);
        setCountry(initialValues.country);
        setStateProvince(initialValues.stateProvince);
      } catch (error) {
        console.error(error);
      }
    };
    getInfo();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    let hasErrors = false;
    setSubmitted(true);

    if (!companyName) {
      setCompanyNameError('required field');
      hasErrors = true;
    } else {
      setCompanyNameError('');
    }
    if (!companyAddressLine1) {
      setCompanyAddressLine1Error('required field');
      hasErrors = true;
    } else {
      setCompanyAddressLine1Error('');
    }

    if (!city) {
      setCityError('required field');
      hasErrors = true;
    } else {
      setCityError('');
    }

    if (!stateProvince) {
      setStateInvalid(true);
      hasErrors = true;
    } else {
      setStateInvalid(false);
    }

    if (!zipCode) {
      setZipCodeError('required field');
      hasErrors = true;
    } else if (!checkZipCode(zipCode, country)) {
      hasErrors = true;
      setZipCodeError('invalid!');
    } else { 
      setZipCodeError(''); 
    }

    if (!firstName) {
      setFirstNameError('required field');
      hasErrors = true;
    } else {
      setFirstNameError('');
    }

    if (!lastName) {      
      setLastNameError('required field');
      hasErrors = true;
    } else {
      setLastNameError('');
    }

    if (!phoneNumber || phoneNumber === '+1') {
      setPhoneError('required field');
      hasErrors = true;
    } else {
      setPhoneError('');
    }

    if (form.checkValidity() === false || hasErrors) {
      event.stopPropagation();
    } else {
      setIsSaving(true);
      const editRequest = async () => {
        try {
          let tempZipCode = zipCode;
          if (country.value === '38') {
            // remove the gap in the middle
            tempZipCode = tempZipCode.replace(' ', '');
          }
          const editBody = {
            ClientName: companyName,
            CompanyAddressLine1: companyAddressLine1,
            CompanyAddressLine2: companyAddressLine2,
            City: city,
            StateAndProvince: stateProvince.value,
            Country: country.label,
            CountryId: country.value,
            ZipCode: tempZipCode,
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            PhoneNumber: phoneNumber.slice(2),
          };
          const ediData = await api.post(`Client/SelfSignUpClientUser`, editBody);
          if(!ediData.data.Success){
            setErrorVisible(true);
            setErrorMessage(ediData.data.Error);  
            return
          }
          setInitialValues({
            ...initialValues,
            companyName,
            companyAddressLine1,
            companyAddressLine2,
            city,
            zipCode,
            firstName,
            lastName,
            email,
            phoneNumber,
            country,
            stateProvince,
          });
          setIsChanged(false);
          setShowUpdateSuccess(true);
          setIsDisable(true);
        } catch (error) {
          console.error(error);
        }
      }
      editRequest();
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setShowUpdateSuccess(false);
  }

  const handleCountryChange = (selectedOption) => {
    setCountry(selectedOption);
    setStateProvince(null);
  };

  const handleStateChange = (selectedOption) => {
    setStateProvince(selectedOption);
    setStateInvalid(false);
  };

  const handleEdit = () => {
    setIsDisable(!isDisable);
  }
  const handleCancel = () => {
    setIsDisable(!isDisable);
  }

  const handleZipCodeChange = (e) => {
    const { value } = e.target;
    let formattedValue = value;
    let zipCodeRegex;
  
    if (country.value === '1') {
      formattedValue = value.replace(/\D/g, '').slice(0, 5);
      zipCodeRegex = /^\d{5}(-\d{4})?$/;
    } else if (country.value === '38') {
      formattedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (formattedValue.length > 3) {
        formattedValue = formattedValue.slice(0, 3) + ' ' + formattedValue.slice(3, 6);
      }
      zipCodeRegex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
    }
    setZipCode(formattedValue); 
    if (validated) {
      if (!formattedValue) {
        setZipCodeError('required field');
      } else if (!zipCodeRegex.test(formattedValue)) {
        setZipCodeError('invalid!');
      } else { 
        setZipCodeError(''); 
      }
    }
  };

  const checkIfChanged = (newCountry, newState, newPhoneNumber) => {
    if (
      companyName !== initialValues.companyName ||
      companyAddressLine1 !== initialValues.companyAddressLine1 ||
      companyAddressLine2 !== initialValues.companyAddressLine2 ||
      city !== initialValues.city ||
      zipCode !== initialValues.zipCode ||
      firstName !== initialValues.firstName ||
      lastName !== initialValues.lastName ||
      email !== initialValues.email ||
      newPhoneNumber !== initialValues.phoneNumber ||
      (newCountry && newCountry.value !== initialValues.country.value) ||
      (newState && newState.value !== initialValues.stateProvince?.value)
    ) {
      setIsChanged(true);
    } else {
      setIsChanged(false);
    }
  };

  const handleInputChange = (setter, errorSetter) => (e) => {
    const { value } = e.target;
    setter(value);
    if (validated) {
      if (!value) {
        errorSetter('required field');
      } else {
        errorSetter('');
      }
    }
  };

  useEffect(() => {
    checkIfChanged(country, stateProvince, phoneNumber);
  }, [country, stateProvince, companyName, companyAddressLine1, companyAddressLine2, city, zipCode, firstName, lastName, email]);

  const handlePhoneNumberChange = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber);
    checkIfChanged(country, stateProvince, newPhoneNumber);
  };

  return (
    <div className="account-info-container">
      <DashboardHeader title="Account Information" />
      <div className="form-container">
        <Form className= "form-content" noValidate validated={validated} onSubmit={handleSubmit}>
          <Form.Group controlId="validationCustom01">
            <div className='form-group-inline'>
              <Form.Label>Company Name</Form.Label>
              {companyNameError && <div className="invalid-feedback" style={{ display: 'block' }}>{companyNameError}</div>}
            </div>
            <Form.Control
              required
              type="text"
              placeholder="Enter your company name"
              name="companyName"
              disabled={isDisable||!clientUser.IsAdmin}
              value={companyName}
              onChange={handleInputChange(setCompanyName, setCompanyNameError)}
              className="step-inputBox"
            />
          </Form.Group>

          <Form.Group controlId="validationCustom02">
            <div className='form-group-inline'>
              <Form.Label>Company Address Line 1*</Form.Label>
              <div className="invalid-feedback" style={{ display: companyAddressLine1Error ? 'block' : 'none' }}>
                {companyAddressLine1Error}
              </div> 
            </div>
            <Form.Control
              required
              type="text"
              placeholder="Enter your company address"
              name="companyAddressLine1"
              disabled={isDisable||!clientUser.IsAdmin}
              value={companyAddressLine1}
              onChange={handleInputChange(setCompanyAddressLine1, setCompanyAddressLine1Error)}
              className="step-inputBox"
            />
          </Form.Group>

          <Form.Group controlId="validationCustom03">
            <div className='form-group-inline'>
              <Form.Label>Company Address Line 2</Form.Label>
            </div>
            <Form.Control
              type="text"
              placeholder="Enter your company address"
              name="companyAddressLine2"
              value={companyAddressLine2}
              disabled={isDisable||!clientUser.IsAdmin}
              onChange={(e) => {
                setCompanyAddressLine2(e.target.value);
              }}
              className="step-inputBox"
            />
          </Form.Group>

          <Row className="step-row">
            <Form.Group as={Col} controlId="validationCustom04">
              <div className='form-group-inline'>
                <Form.Label>City*</Form.Label>
                <div className="invalid-feedback" style={{ display: cityError ? 'block' : 'none' }}>
                  {cityError}
                </div>
              </div>
              <Form.Control
                required
                type="text"
                placeholder="City"
                name="city"
                value={city}
                disabled={isDisable||!clientUser.IsAdmin}
                onChange={handleInputChange(setCity, setCityError)}
                className='step-inputBox'
              />
            </Form.Group>

            <Form.Group as={Col} controlId="validationCustom05">
              <div className='form-group-inline'>
                <Form.Label>State/Province*</Form.Label>
                <div className="invalid-feedback" style={{ display: stateInvalid ? 'block' : 'none' }}>
                  required field
                </div>
              </div>
              <CustomSelect
                disabled={isDisable||!clientUser.IsAdmin}
                key={country.value}
                options={filteredStates}
                placeholder="Select a state/province"
                onSelect={(value) => {
                  const selectedState = filteredStates.find(s => s.value === value);
                  setStateProvince(selectedState);
                  setStateInvalid(false);
                }}
                value={stateProvince?.value}
              />
            </Form.Group>
          </Row>

          <Row className="step-row">
            <Form.Group as={Col} controlId="validationCustom06">
              <div className="form-group-inline">
                <Form.Label>Country*</Form.Label>
              </div>
              <CustomSelect
                options={countries}
                disabled={isDisable||!clientUser.IsAdmin}
                placeholder="Select a country"
                onSelect={(value) => {
                  const selectedCountry = countries.find(c => c.value === value);
                  if (country.value !== selectedCountry.value) {
                    setCountry(selectedCountry);
                    setStateProvince(null);
                    setZipCode('');
                  }
                }}
                value={country?.value}
                defaultValue="1"
              />
            </Form.Group>
            <Form.Group as={Col} controlId="validationCustom07">
              <div className="form-group-inline">
                <Form.Label>Zip Code*</Form.Label>
                <div className="invalid-feedback" style={{ display: zipCodeError ? 'block' : 'none' }}>
                  {zipCodeError}
                </div>
              </div>
              <Form.Control
                required
                type="text"
                placeholder="Zip/Postal code"
                name="zipCode"
                value={zipCode}
                disabled={isDisable||!clientUser.IsAdmin}
                onChange={handleZipCodeChange}
                className="step-inputBox"
              />
            </Form.Group>
          </Row>

          <Row className='step-row'>
            <Form.Group as={Col} controlId="firstNameForm">
              <div className="form-group-inline">
                <Form.Label>Contact First Name</Form.Label>
                {firstNameError && <div className="invalid-feedback" style={{ display: 'block' }}>{firstNameError}</div>}
              </div>
              <Form.Control
                required
                type="text"
                placeholder="Enter your first name"
                value={firstName}
                disabled={isDisable||!clientUser.IsAdmin}
                onChange={handleInputChange(setFirstName)}
                className='step-inputBox'
              />
            </Form.Group>

            <Form.Group as={Col} controlId="lastNameForm">
              <div className="form-group-inline">
                <Form.Label>Contact Last Name</Form.Label>
                {lastNameError && <div className="invalid-feedback" style={{ display: 'block' }}>{lastNameError}</div>}
              </div>
              <Form.Control
                required
                type="text"
                placeholder="Enter your last name"
                value={lastName}
                disabled={isDisable||!clientUser.IsAdmin}
                onChange={handleInputChange(setLastName)}
                className='step-inputBox'
              />
            </Form.Group>
          </Row>

          <Row className='step-row'>
            <Form.Group as={Col} controlId="emailForm">
              <div className="form-group-inline">
                <Form.Label>Email</Form.Label>
              </div>
              <Form.Control
                required
                disabled
                type="text"
                placeholder="Enter your email"
                value={email}
                className='step-inputBox'
              />
            </Form.Group>

            <Form.Group as={Col} controlId="phoneNumberForm">
              <div className="Accountinfo-phoneNumber">
                <PhoneNumber
                  onPhoneNumberChange={handlePhoneNumberChange}
                  phoneError={phoneError}
                  value={phoneNumber}
                  disabled={isDisable||!clientUser.IsAdmin}
                  submitted={submitted}
                />
              </div>
            </Form.Group>
          </Row>
          <div className='Account-info-save-btn'>
          {
            clientUser.IsAdmin ? (
              (isDisable) ? 
              <Buttons text="Edit" handleSubmit={handleEdit} /> :
              <div>
                <Buttons back backText="Cancel" handleBack={handleCancel} text="Confirm"/>
              </div>
              
            ) : (
              <div style={{height: "80px"}}>

              </div>

            )
          }
          </div>
        </Form>
      </div>
      <Popup 
        show={showUpdateSuccess}
        handleClose={handleClose}
        title={'Account Information Updated'}
        titleStyle="popup-title"
        body={
          <>
            <p>Your account information has been successfully updated.</p>
            <div className='account-update-button'>
              <Buttons text={'Done'} handleSubmit={handleClose} />
            </div>
          </>
        }
      />
    </div>
  );
}

export default AccountInformation;
